import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { guidesApi, documentsApi, ApiRequestError } from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import { marked } from 'marked';

export default function GuideEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [requiresBooking, setRequiresBooking] = useState(false);

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;

    const loadGuide = async () => {
      setIsLoading(true);
      try {
        // We need to get the guide by ID, but our API uses slug
        // So we'll fetch the list and find by ID
        const result = await guidesApi.list();
        const guide = result.guides.find((g) => g.id === id);
        if (!guide) {
          setError('Guide not found');
          return;
        }

        // Now get the full guide content by slug
        const fullGuide = await guidesApi.get(guide.slug);
        setTitle(fullGuide.guide.title);
        setSlug(fullGuide.guide.slug);
        setContent(fullGuide.guide.content || '');
        setIsPublished(fullGuide.guide.isPublished);
        setRequiresBooking(fullGuide.guide.requiresBooking);
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to load guide');
        }
      } finally {
        setIsLoading(false);
      }
    };
    loadGuide();
  }, [id]);

  // Warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    setHasUnsavedChanges(true);
    // Auto-generate slug for new guides
    if (!isEditing) {
      const newSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 50);
      setSlug(newSlug);
    }
  };

  const handleContentChange = (value: string) => {
    setContent(value);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (isEditing) {
        await guidesApi.update(id!, {
          title,
          slug,
          content,
          isPublished,
          requiresBooking,
        });
      } else {
        const result = await guidesApi.create({
          title,
          content,
          isPublished,
          requiresBooking,
        });
        // Navigate to edit page with new ID
        navigate(`/admin/guides/${result.guide.id}/edit`, { replace: true });
      }
      setHasUnsavedChanges(false);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to save guide');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    setIsSaving(true);
    setError(null);

    try {
      if (isEditing) {
        await guidesApi.update(id!, {
          title,
          slug,
          content,
          isPublished: true,
          requiresBooking,
        });
        setIsPublished(true);
        setHasUnsavedChanges(false);
      } else {
        const result = await guidesApi.create({
          title,
          content,
          isPublished: true,
          requiresBooking,
        });
        navigate(`/admin/guides/${result.guide.id}/edit`, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to publish guide');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportMD = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.md') && !file.name.endsWith('.markdown')) {
      setError('Please select a Markdown file (.md or .markdown)');
      return;
    }

    try {
      const text = await file.text();

      // Configure marked for safe HTML output
      marked.setOptions({
        gfm: true, // GitHub Flavored Markdown
        breaks: true, // Convert line breaks to <br>
      });

      // Convert markdown to HTML
      const html = await marked.parse(text);

      // Extract title from first H1 if present and title is empty
      if (!title.trim()) {
        const h1Match = text.match(/^#\s+(.+)$/m);
        if (h1Match) {
          handleTitleChange(h1Match[1].trim());
        } else {
          // Use filename without extension as title
          const fileName = file.name.replace(/\.(md|markdown)$/i, '');
          handleTitleChange(fileName);
        }
      }

      // Set the content (will merge with or replace existing)
      setContent(html);
      setHasUnsavedChanges(true);
      setError(null);
    } catch (err) {
      setError('Failed to parse Markdown file');
      console.error('MD import error:', err);
    }

    // Reset file input so the same file can be re-selected
    e.target.value = '';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/guides"
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Guide' : 'New Guide'}
          </h1>
          {isPublished && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Published
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <span className="text-sm text-amber-600">Unsaved changes</span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.markdown"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={handleImportMD}
            disabled={isSaving}
            className="btn-secondary flex items-center gap-2"
            title="Import Markdown file"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import MD
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-secondary"
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          {!isPublished && (
            <button
              onClick={handlePublish}
              disabled={isSaving}
              className="btn-primary"
            >
              Publish
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Title */}
          <div className="card p-4">
            <label htmlFor="title" className="label">
              Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              className="input text-lg font-medium"
              placeholder="Enter guide title..."
            />
          </div>

          {/* Content */}
          <div className="card p-4">
            <label className="label mb-2">Content</label>
            {isUploadingImage && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                Uploading image...
              </div>
            )}
            <RichTextEditor
              content={content}
              onChange={handleContentChange}
              placeholder="Write your guide content here..."
              onUploadImage={async (file) => {
                setIsUploadingImage(true);
                try {
                  const viewUrl = await documentsApi.uploadImage(file);
                  return viewUrl;
                } finally {
                  setIsUploadingImage(false);
                }
              }}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Settings */}
          <div className="card p-4 space-y-4">
            <h3 className="font-medium text-gray-900">Settings</h3>

            {/* Slug (only editable for existing guides) */}
            {isEditing && (
              <div>
                <label htmlFor="slug" className="label">
                  URL Slug
                </label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-1">/guide/</span>
                  <input
                    id="slug"
                    type="text"
                    value={slug}
                    onChange={(e) => {
                      setSlug(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    className="input flex-1"
                    pattern="[a-z0-9-]+"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Only lowercase letters, numbers, and hyphens
                </p>
              </div>
            )}

            {/* Publish status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Published</span>
              <button
                type="button"
                onClick={() => {
                  setIsPublished(!isPublished);
                  setHasUnsavedChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  isPublished ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isPublished ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Requires booking */}
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-700">Requires Booking</span>
                <p className="text-xs text-gray-500">
                  Only visible to users with active bookings
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setRequiresBooking(!requiresBooking);
                  setHasUnsavedChanges(true);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  requiresBooking ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    requiresBooking ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Preview link */}
          {isEditing && slug && (
            <div className="card p-4">
              <h3 className="font-medium text-gray-900 mb-2">Preview</h3>
              <Link
                to={`/guide/${slug}`}
                className="text-sm text-primary-600 hover:text-primary-700"
                target="_blank"
              >
                View guide page &rarr;
              </Link>
            </div>
          )}

          {/* Help */}
          <div className="card p-4 bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-2">Formatting Tips</h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>Use headings to organize content</li>
              <li>Create lists for step-by-step instructions</li>
              <li>Add links to external resources</li>
              <li>Use bold/italic for emphasis</li>
              <li>Click the image button to upload photos inline</li>
            </ul>
          </div>

          {/* Import help */}
          <div className="card p-4 bg-blue-50">
            <h3 className="font-medium text-blue-900 mb-2">Import from Markdown</h3>
            <p className="text-xs text-blue-700">
              Click "Import MD" to upload a .md file. The markdown will be converted to rich text.
              If no title is set, it will be extracted from the first heading or filename.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
